import { z } from 'zod';
import { contrastRatio } from './platform.ts';

/**
 * Tema do sistema (Configuração › Guia visual): o arquivo `base-theme` que o questionário de
 * design gera e a tela importa e exporta. Ele traz as cores do tema claro e do escuro, os cantos,
 * a altura dos controles, o título da tela e cinco escolhas de estilo. A moldura transforma tudo
 * em variáveis CSS (`themeCss`); as telas só usam as classes do tema (docs/guia-visual/README.md).
 */

/** As cores que o tema pode definir: os nomes das variáveis do styles.css, sem o `--`. */
export const THEME_COLOR_TOKENS = [
  'paper',
  'sheet',
  'ink',
  'muted',
  'rule',
  'control-border',
  'accent',
  'on-accent',
  'brand',
  'brand-soft',
  'brand-text',
  'danger',
  'ok',
  'info',
  'gold',
  'violet',
  'tonal',
  'sidebar',
  'sidebar-text',
  'sidebar-muted',
  'sidebar-rule',
  'sidebar-control',
  'sidebar-hover',
  'sidebar-active',
  'sidebar-active-text',
  'sidebar-link',
] as const;
export type ThemeColorToken = (typeof THEME_COLOR_TOKENS)[number];
export type ThemeColors = Record<ThemeColorToken, string>;

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/);
const px = (min: number, max: number) =>
  z
    .string()
    .regex(/^\d{1,2}px$/)
    .refine((v) => {
      const n = parseInt(v, 10);
      return n >= min && n <= max;
    }, `Entre ${min}px e ${max}px`);

/** Cores de um tema: só os nomes conhecidos; a que faltar fica com o padrão do beadev. */
const colorsSchema = z
  .object(Object.fromEntries(THEME_COLOR_TOKENS.map((k) => [k, hex.optional()])))
  .strip() as z.ZodType<Partial<ThemeColors>>;

export const themeStyleSchema = z.object({
  /** Cartões e tabelas: borda fina ou sombra. */
  panel: z.enum(['border', 'shadow']),
  /** Cabeçalho das tabelas: discreto ou com fundo. */
  tableHeader: z.enum(['plain', 'filled']),
  /** Linha embaixo do título da tela. */
  pageHeaderRule: z.boolean(),
  /** Etiquetas de situação: preenchidas ou contorno com ponto. */
  badge: z.enum(['soft', 'outline']),
  /** Botão secundário: contorno ou fundo suave. */
  secondaryButton: z.enum(['outline', 'tonal']),
});

/**
 * Letra do sistema: a da marca (Geist, que vem com a base) ou uma das fontes do próprio aparelho
 * (nada é baixado de fora). Escolhida pelo cliente no Smart Builder ou no Guia visual.
 */
export const THEME_FONTS = ['base', 'modern', 'classic', 'friendly'] as const;
export type ThemeFont = (typeof THEME_FONTS)[number];
export const THEME_FONT_STACKS: Record<Exclude<ThemeFont, 'base'>, string> = {
  modern: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  classic: 'Georgia, Cambria, "Times New Roman", serif',
  friendly: '"Trebuchet MS", Verdana, "Segoe UI", sans-serif',
};

/** Onde fica o menu nas telas de computador: na lateral (padrão) ou numa faixa no topo. */
export const THEME_MENUS = ['side', 'top'] as const;
export type ThemeMenu = (typeof THEME_MENUS)[number];

/** O arquivo de tema (formato `base-theme`, versão 1). Campos que não são do tema são ignorados. */
export const themeFileSchema = z.object({
  format: z.literal('base-theme'),
  version: z.literal(1),
  name: z.string().trim().min(1).max(60),
  colors: z.object({ light: colorsSchema, dark: colorsSchema }),
  radius: z.object({ sm: px(0, 12), md: px(0, 16) }),
  controlHeight: px(32, 44),
  /** Altura do logo no topo da lateral. */
  logoHeight: px(20, 56).default('36px'),
  pageTitle: z.object({
    size: px(20, 32),
    weight: z.union([z.literal(500), z.literal(600), z.literal(700)]),
  }),
  style: themeStyleSchema,
  /** Arquivos antigos, sem estes dois campos, continuam valendo (a letra da marca, menu ao lado). */
  font: z.enum(THEME_FONTS).default('base'),
  menu: z.enum(THEME_MENUS).default('side'),
});
export type ThemeFile = z.output<typeof themeFileSchema>;

/**
 * O tema padrão do beadev: a família visual do theo (fundo quase preto, verde-limão #c4ef2f no
 * botão principal e no item marcado, roxo #6b4ad9 nos links e destaques de texto). Lateral escura,
 * cantos levemente arredondados, controles compactos, cartões com sombra, cabeçalho de tabela
 * marcado, título com linha, etiquetas preenchidas e botão secundário de fundo suave.
 */
export const DEFAULT_THEME: ThemeFile = {
  format: 'base-theme',
  version: 1,
  name: 'beadev',
  colors: {
    light: {
      paper: '#f5f6f8',
      sheet: '#ffffff',
      ink: '#0b0e14',
      muted: '#5f6676',
      rule: '#e3e6eb',
      'control-border': '#858c9a',
      accent: '#c4ef2f',
      'on-accent': '#0b0e14',
      brand: '#6b4ad9',
      'brand-soft': '#ede8fb',
      'brand-text': '#5636c4',
      danger: '#b42318',
      ok: '#2a7247',
      info: '#2b5a96',
      gold: '#e6b65d',
      violet: '#6b4ad9',
      tonal: '#eceef2',
      sidebar: '#0b0e14',
      'sidebar-text': '#fafbfc',
      'sidebar-muted': '#8b93a3',
      'sidebar-rule': '#1f2533',
      'sidebar-control': '#3a4152',
      'sidebar-hover': '#141a28',
      'sidebar-active': '#c4ef2f',
      'sidebar-active-text': '#0b0e14',
      'sidebar-link': '#c4ef2f',
    },
    dark: {
      paper: '#0b0e14',
      sheet: '#141a28',
      ink: '#fafbfc',
      muted: '#9aa1b0',
      rule: '#252c3c',
      'control-border': '#6b7385',
      accent: '#c4ef2f',
      'on-accent': '#0b0e14',
      brand: '#a78bfa',
      'brand-soft': '#251d47',
      'brand-text': '#b9a3ff',
      danger: '#f08a8a',
      ok: '#6cc495',
      info: '#7fb0ea',
      gold: '#e6b65d',
      violet: '#a78bfa',
      tonal: '#1c2232',
      sidebar: '#07090e',
      'sidebar-text': '#fafbfc',
      'sidebar-muted': '#8b93a3',
      'sidebar-rule': '#1c2232',
      'sidebar-control': '#3a4152',
      'sidebar-hover': '#121726',
      'sidebar-active': '#c4ef2f',
      'sidebar-active-text': '#0b0e14',
      'sidebar-link': '#c4ef2f',
    },
  },
  radius: { sm: '6px', md: '8px' },
  controlHeight: '34px',
  logoHeight: '32px',
  pageTitle: { size: '24px', weight: 600 },
  style: {
    panel: 'shadow',
    tableHeader: 'filled',
    pageHeaderRule: true,
    badge: 'soft',
    secondaryButton: 'tonal',
  },
  font: 'base',
  menu: 'side',
};

/** Lê um arquivo de tema (texto JSON). Erro com a mensagem para a pessoa quando não serve. */
export function parseThemeFile(
  text: string,
): { ok: true; theme: ThemeFile } | { ok: false; reason: 'json' | 'format'; issues: string[] } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'json', issues: [] };
  }
  const r = themeFileSchema.safeParse(raw);
  if (!r.success)
    return {
      ok: false,
      reason: 'format',
      issues: r.error.issues.map((i) => `${i.path.join('.') || '(arquivo)'}: ${i.message}`),
    };
  return { ok: true, theme: r.data };
}

/** As cores completas de um modo: as do tema por cima das do padrão. */
export function themeColors(theme: ThemeFile, mode: 'light' | 'dark'): ThemeColors {
  return { ...(DEFAULT_THEME.colors[mode] as ThemeColors), ...theme.colors[mode] } as ThemeColors;
}

/** Pares de texto e fundo que precisam de leitura: [texto, fundo, mínimo]. */
const CONTRAST_PAIRS: [ThemeColorToken, ThemeColorToken, number][] = [
  ['ink', 'paper', 4.5],
  ['ink', 'sheet', 4.5],
  ['muted', 'paper', 4.5],
  ['muted', 'sheet', 4.5],
  ['brand-text', 'sheet', 4.5],
  ['brand-text', 'brand-soft', 4.5],
  ['on-accent', 'accent', 4.5],
  ['control-border', 'sheet', 3],
  ['danger', 'sheet', 4.5],
  ['ok', 'sheet', 4.5],
  ['info', 'sheet', 4.5],
  ['sidebar-text', 'sidebar', 4.5],
  ['sidebar-muted', 'sidebar', 4.5],
  ['sidebar-link', 'sidebar', 4.5],
  ['sidebar-active-text', 'sidebar-active', 4.5],
];

export interface ContrastWarning {
  mode: 'light' | 'dark';
  text: ThemeColorToken;
  background: ThemeColorToken;
  ratio: number;
  minimum: number;
}

/** Combinações do tema que ficam difíceis de ler (contraste abaixo do mínimo do guia). */
export function themeContrastWarnings(theme: ThemeFile): ContrastWarning[] {
  const out: ContrastWarning[] = [];
  for (const mode of ['light', 'dark'] as const) {
    const c = themeColors(theme, mode);
    for (const [text, background, minimum] of CONTRAST_PAIRS) {
      const ratio = contrastRatio(c[text], c[background]);
      if (ratio < minimum)
        out.push({ mode, text, background, ratio: Math.round(ratio * 100) / 100, minimum });
    }
  }
  return out;
}

/** As variáveis de medida e estilo (iguais nos dois modos). */
export function themeShapeVars(theme: ThemeFile): Record<string, string> {
  const s = theme.style;
  return {
    ...(theme.font !== 'base' && { '--font-sans': THEME_FONT_STACKS[theme.font] }),
    '--radius-md': theme.radius.sm,
    '--radius-lg': theme.radius.md,
    '--control-height': theme.controlHeight,
    '--logo-height': theme.logoHeight,
    '--page-title-size': theme.pageTitle.size,
    '--page-title-weight': String(theme.pageTitle.weight),
    '--panel-border': s.panel === 'shadow' ? 'transparent' : 'var(--rule)',
    '--panel-shadow':
      s.panel === 'shadow'
        ? '0 1px 3px rgb(0 0 0 / 0.08), 0 4px 14px rgb(0 0 0 / 0.06)'
        : '0 1px 2px rgb(23 32 43 / 0.06), 0 1px 3px rgb(23 32 43 / 0.04)',
    '--thead-bg': s.tableHeader === 'filled' ? 'var(--paper)' : 'var(--sheet)',
    '--thead-text': s.tableHeader === 'filled' ? 'var(--ink)' : 'var(--muted)',
    '--thead-weight': s.tableHeader === 'filled' ? '600' : '500',
    '--page-header-rule': s.pageHeaderRule ? 'var(--rule)' : 'transparent',
    '--button-secondary-bg': s.secondaryButton === 'tonal' ? 'var(--tonal)' : 'var(--sheet)',
    '--button-secondary-border':
      s.secondaryButton === 'tonal' ? 'transparent' : 'var(--control-border)',
  };
}

/**
 * A folha de estilo do tema: as cores do claro em `:root`, as do escuro em
 * `:root[data-theme='dark']`, e as medidas. `data-badge` vai no `<html>` (as etiquetas mudam de
 * forma, não só de cor). Usada pela moldura e pela prévia do Guia visual.
 */
export function themeCss(theme: ThemeFile, selector = ':root'): string {
  const decl = (vars: Record<string, string>) =>
    Object.entries(vars)
      .map(([k, v]) => `${k}:${v};`)
      .join('');
  const colorVars = (mode: 'light' | 'dark') =>
    Object.fromEntries(Object.entries(themeColors(theme, mode)).map(([k, v]) => [`--${k}`, v]));
  return (
    `${selector}{${decl(colorVars('light'))}${decl(themeShapeVars(theme))}}` +
    `${selector}[data-theme='dark']{${decl(colorVars('dark'))}}`
  );
}
